import { Point } from "pixi.js";
import { ControlConstants } from "../../game/control/constant/ControlConstants";
import { FontShadow, FontStroke } from "../../game/control/properties/FontProperties";
import { Logger } from "../log/Logger";
import { StringUtil } from "../string/StringUtil";
import { Delay } from "../time/Delay";
import { WebUtil } from "../web/WebUtil";
import { MouseUtil } from "./MouseUtil";
/**
 * LocalisationConstants.ts
 */
export class LocalisationConstants {
}
LocalisationConstants.UPDATE_BOUNDS = "updateBounds";
LocalisationConstants.UPDATE_FONT = "updateFont";
LocalisationConstants.UPDATE_STROKE = "updateStroke";
LocalisationConstants.UPDATE_DROP_SHADOW = "updateDropShadow";
/**
 * LayoutUtil.ts
 */
export class LayoutUtil {
    static enableDrag(value) {
        MouseUtil.setInteractive(value, true);
        MouseUtil.addMouseDown(value, this.handleStartDrag, this);
        MouseUtil.addMouseUp(value, this.handleEndDrag, this);
        MouseUtil.addMouseMove(value, this.handleDrag, this);
    }
    static handleStartDrag(event) {
        this.target = event.target;
        this.delay = Delay.create(150, this.startDrag, this);
    }
    static startDrag() {
        this.isDragging = true;
    }
    static handleDrag(event) {
        if (this.isDragging && this.target) {
            let newPosition = event.getLocalPosition(this.target.parent);
            this.target.position.copyFrom(newPosition);
            this.target.updateTransform();
        }
    }
    static handleEndDrag() {
        if (this.delay) {
            this.delay.dispose();
        }
        this.output();
        this.isDragging = false;
        this.delay = null;
    }
    static output() {
        if (this.hasActiveField()) {
            this.outputField();
        }
        if (this.target) {
            Logger.forceLog("\nPosition: " + this.target.x + ":" + this.target.y);
        }
    }
    /* Localisation Tooling */
    static handleMessage(event) {
        if (event.data) {
            let data = (typeof event.data === "object") ? event.data : JSON.parse(event.data);
            let type = data.type;
            if (type == LocalisationConstants.UPDATE_BOUNDS) {
                this.updateBounds(data);
            }
            else if (type == LocalisationConstants.UPDATE_FONT) {
                this.updateFont(data);
            }
            else if (type == LocalisationConstants.UPDATE_STROKE) {
                this.updateStroke(data);
            }
            else if (type == LocalisationConstants.UPDATE_DROP_SHADOW) {
                this.updateDropShadow(data);
            }
        }
    }
    static outputField() {
        let field = this.target;
        let properties = field.getProperties();
        let fontProperties = properties.fontProperties;
        let bounds = field.getTextBounds();
        let data = {};
        data.type = "outputField";
        data.x = this.target.x;
        data.y = this.target.y;
        data.width = bounds.width;
        data.height = bounds.height;
        data.multiline = properties.multiline;
        data.fontSize = fontProperties.size;
        data.fontColour = fontProperties.colour;
        data.fontHorizontalAlign = properties.horizontalAlign;
        data.fontVerticalAlign = properties.verticalAlign;
        data.fontLeading = fontProperties.leading;
        data.fontUppercase = fontProperties.uppercase;
        let stroke = fontProperties.stroke;
        if (stroke) {
            data.strokeColour = stroke.colour;
            data.strokeThickness = stroke.thickness;
        }
        let dropShadow = fontProperties.dropShadow;
        if (dropShadow) {
            data.dropShadowColour = dropShadow.colour;
            data.dropShadowDistance = dropShadow.distance;
            data.dropShadowAngle = dropShadow.angle;
            if (dropShadow.alpha < 1) {
                data.dropShadowAlpha = dropShadow.alpha;
            }
            if (dropShadow.blur > 0) {
                data.dropShadowBlur = dropShadow.blur;
            }
        }
        let message = JSON.stringify(data);
        if (window.opener) {
            window.opener.postMessage(message, "*");
        }
    }
    static updateBounds(data) {
        if (this.hasActiveField()) {
            let width = parseInt(data.width);
            let height = parseInt(data.height);
            let multiline = data.multiline;
            let showBounds = data.visible == true;
            let textField = this.getTextField();
            const isLocalise = StringUtil.isTrue(WebUtil.getUrlParameter(ControlConstants.LOCALISE));
            if (isLocalise) {
                textField.getTextBounds().visible = showBounds;
            }
            textField.refreshBounds(width, height);
            let properties = this.getTextFieldProperties();
            properties.multiline = multiline;
            properties.size = new Point(width, height);
            this.refresh();
            this.output();
        }
    }
    static updateFont(data) {
        if (this.hasActiveField()) {
            let size = parseInt(data.size);
            let colour = data.colour;
            let horizontalAlign = data.horizontalAlign;
            let verticalAlign = data.verticalAlign;
            let leading = parseInt(data.leading);
            let uppercase = data.uppercase;
            let textFieldProperties = this.getTextFieldProperties();
            let fontProperties = this.getFontProperties();
            textFieldProperties.horizontalAlign = horizontalAlign;
            textFieldProperties.verticalAlign = verticalAlign;
            fontProperties.size = size;
            fontProperties.colour = colour;
            fontProperties.uppercase = uppercase;
            if (leading != 0) {
                fontProperties.leading = leading;
            }
            this.refresh();
            this.output();
        }
    }
    static updateStroke(data) {
        if (this.hasActiveField()) {
            let colour = data.colour;
            let thickness = parseInt(data.thickness);
            let fontProperties = this.getFontProperties();
            fontProperties.addStroke(new FontStroke(colour, thickness));
            this.refresh();
            this.output();
        }
    }
    static updateDropShadow(data) {
        if (this.hasActiveField()) {
            let colour = data.colour;
            let distance = parseInt(data.distance);
            let angle = parseInt(data.angle);
            let alpha = parseFloat(data.alpha);
            let blur = parseFloat(data.blur);
            let fontProperties = this.getFontProperties();
            fontProperties.addDropShadow(new FontShadow(colour, distance, angle, alpha, blur));
            this.refresh();
            this.output();
        }
    }
    static refresh() {
        let field = this.getTextField();
        field.setText(field.getText(), true);
    }
    static getTextFieldProperties() {
        let field = this.getTextField();
        return field.getProperties();
    }
    static getFontProperties() {
        let properties = this.getTextFieldProperties();
        return properties.fontProperties;
    }
    static getTextField() {
        return this.target;
    }
    static hasActiveField() {
        let has = false;
        let tf;
        if (this.target) {
            tf = this.target;
            if (tf.getProperties) {
                has = true;
            }
        }
        if (has) {
            return tf;
        }
        return null;
    }
}
LayoutUtil.isDragging = false;
//# sourceMappingURL=LayoutUtil.js.map